import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Save, Zap, XCircle, Radio, Truck } from "lucide-react";

const inpCls = "font-mono text-sm";

/**
 * Tata Motors Fleet Edge integration UI — used inside Settings → Live Tracking
 * when the selected source is (or includes) tata_fleet. Extracted from
 * Integrations.jsx so tracking-source config lives in one screen.
 */
export default function TataFleetSection() {
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useQuery({
    queryKey: ["tata-fleet-config"],
    queryFn: () => api.get("/tata-fleet/config").then((r) => r.data),
  });
  const { data: enrolled } = useQuery({
    queryKey: ["tata-fleet-enrolled"],
    queryFn: () => api.get("/tata-fleet/enrolled").then((r) => r.data),
  });
  const { data: bowsers } = useQuery({
    queryKey: ["fleet-bowsers-tata"],
    queryFn: () => api.get("/fleet/bowsers").then((r) => r.data),
  });

  const [f, setF] = useState(null);
  const [enrollBowserId, setEnrollBowserId] = useState("");
  const [enrollVin, setEnrollVin] = useState("");

  React.useEffect(() => {
    if (cfg && !f) {
      setF({
        enabled: !!cfg.enabled,
        client_id: cfg.client_id || "",
        client_secret: cfg.client_secret || "",
        webhook_secret: cfg.webhook_secret || "",
        base_url: cfg.base_url || "",
        poll_interval_sec: cfg.poll_interval_sec || 30,
      });
    }
  }, [cfg, f]);

  const save = useMutation({
    mutationFn: (payload) => api.patch("/tata-fleet/config", payload).then((r) => r.data),
    onSuccess: (data) => {
      toast.success("Tata Fleet Edge config saved (encrypted)");
      qc.invalidateQueries({ queryKey: ["tata-fleet-config"] });
      setF({
        enabled: !!data.enabled,
        client_id: data.client_id || "",
        client_secret: data.client_secret || "",
        webhook_secret: data.webhook_secret || "",
        base_url: data.base_url || "",
        poll_interval_sec: data.poll_interval_sec || 30,
      });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const testConn = useMutation({
    mutationFn: () => api.post("/tata-fleet/test").then((r) => r.data),
    onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
    onError: (e) => toast.error(e.response?.data?.detail || "Test failed"),
  });

  const enroll = useMutation({
    mutationFn: () => api.post("/tata-fleet/enroll", { bowser_id: enrollBowserId, tata_vin: enrollVin }),
    onSuccess: () => {
      toast.success("VIN mapped to bowser");
      setEnrollBowserId(""); setEnrollVin("");
      qc.invalidateQueries({ queryKey: ["tata-fleet-enrolled"] });
      qc.invalidateQueries({ queryKey: ["fleet-bowsers-tata"] });
      qc.invalidateQueries({ queryKey: ["fleet"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Enrollment failed"),
  });

  const unenroll = useMutation({
    mutationFn: (bowserId) => api.delete(`/tata-fleet/enroll/${bowserId}`),
    onSuccess: () => {
      toast.success("VIN unmapped");
      qc.invalidateQueries({ queryKey: ["tata-fleet-enrolled"] });
      qc.invalidateQueries({ queryKey: ["fleet-bowsers-tata"] });
      qc.invalidateQueries({ queryKey: ["fleet"] });
    },
  });

  if (isLoading || !f) return <div className="text-sm text-muted-foreground">Loading Tata Fleet Edge…</div>;

  const bowserOptions = (bowsers?.items || []).filter((b) => !b.tata_vin);

  return (
    <div className="space-y-4" data-testid="tata-fleet-section">
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Radio size={18} className="text-primary" /> Tata Motors Fleet Edge · OAuth
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Pull live GPS positions from OBD-II units on Tata bowsers · polled every {f.poll_interval_sec}s.
              Non-Tata bowsers keep using the Driver app or generic GPS device sources above.
              Get OAuth credentials from your Fleet Edge account manager.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">Enable</span>
            <Switch
              checked={!!f.enabled}
              onCheckedChange={(v) => setF({ ...f, enabled: v })}
              data-testid="tata-enabled"
            />
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Client ID</Label>
            <Input
              value={f.client_id}
              onChange={(e) => setF({ ...f, client_id: e.target.value })}
              placeholder="Provided by Tata Fleet Edge"
              data-testid="tata-client-id"
              className={inpCls}
            />
          </div>
          <div>
            <Label>Client Secret</Label>
            <Input
              value={f.client_secret}
              onChange={(e) => setF({ ...f, client_secret: e.target.value })}
              placeholder="Provided by Tata Fleet Edge"
              data-testid="tata-client-secret"
              className={inpCls}
            />
          </div>
          <div>
            <Label>Webhook Secret (optional)</Label>
            <Input
              value={f.webhook_secret}
              onChange={(e) => setF({ ...f, webhook_secret: e.target.value })}
              placeholder="If Tata sends server-side pushes"
              data-testid="tata-webhook-secret"
              className={inpCls}
            />
          </div>
          <div>
            <Label>Base URL <span className="text-xs text-muted-foreground">(override only if instructed)</span></Label>
            <Input
              value={f.base_url}
              onChange={(e) => setF({ ...f, base_url: e.target.value })}
              placeholder="https://api.fleetedge.tatamotors.com"
              data-testid="tata-base-url"
              className={inpCls}
            />
          </div>
          <div>
            <Label>Poll interval (seconds)</Label>
            <Input
              type="number"
              min={10}
              max={300}
              value={f.poll_interval_sec}
              onChange={(e) => setF({ ...f, poll_interval_sec: Number(e.target.value) || 30 })}
              data-testid="tata-poll-interval"
              className={inpCls}
            />
            <p className="mt-1 text-xs text-muted-foreground">Between 10s (aggressive) and 300s (5min). Applies within one poll cycle — no restart.</p>
          </div>
          <div className="md:col-span-2 flex items-center justify-between border-t border-border/60 pt-3">
            <div className="text-xs text-muted-foreground">
              {cfg?.updated_at ? <>Last saved <b>{new Date(cfg.updated_at).toLocaleString()}</b> by {cfg.updated_by || "—"}</> : "Not yet configured"}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => testConn.mutate()}
                disabled={testConn.isPending}
                data-testid="tata-test-btn"
              >
                <Zap size={14} className="mr-1" />
                {testConn.isPending ? "Testing…" : "Test connection"}
              </Button>
              <Button
                onClick={() => save.mutate(f)}
                disabled={save.isPending}
                data-testid="tata-save-btn"
              >
                <Save size={14} className="mr-1" />
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck size={16} className="text-primary" /> Enrolled bowsers ({enrolled?.items?.length || 0})
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            Map each Tata bowser to its VIN as registered on Fleet Edge. Enrollment
            auto-switches that bowser&apos;s tracking source to Tata. Non-Tata bowsers
            (Ashok Leyland, Eicher, Mahindra etc.) stay on Driver-app or GPS-device mode.
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <Label>Bowser</Label>
              <Select value={enrollBowserId} onValueChange={setEnrollBowserId}>
                <SelectTrigger data-testid="tata-enroll-bowser"><SelectValue placeholder="Pick a bowser" /></SelectTrigger>
                <SelectContent>
                  {bowserOptions.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">All bowsers are already enrolled</div>
                  ) : bowserOptions.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.code} · {b.registration_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tata VIN / Chassis ID</Label>
              <Input
                value={enrollVin}
                onChange={(e) => setEnrollVin(e.target.value.toUpperCase())}
                placeholder="e.g. MAT770234NAB12345"
                data-testid="tata-enroll-vin"
                className={inpCls}
              />
            </div>
            <Button
              onClick={() => enroll.mutate()}
              disabled={!enrollBowserId || !enrollVin || enroll.isPending}
              data-testid="tata-enroll-btn"
            >
              {enroll.isPending ? "Adding…" : "Enroll"}
            </Button>
          </div>

          <div className="border-t border-border/40 pt-3">
            {enrolled?.items?.length ? (
              <div className="space-y-2" data-testid="tata-enrolled-list">
                {enrolled.items.map((b) => (
                  <div key={b.id} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-sm" data-testid={`tata-enrolled-${b.code}`}>
                    <div>
                      <div className="font-semibold">{b.code} · {b.registration_no}</div>
                      <div className="text-xs font-mono text-muted-foreground">VIN: {b.tata_vin}</div>
                      <div className="text-xs text-muted-foreground">
                        {b.last_ping_source === "tata_fleet" && b.last_ping_at
                          ? <>Last ping <b>{new Date(b.last_ping_at).toLocaleTimeString()}</b> · lat {b.last_lat?.toFixed(4)}, lng {b.last_lng?.toFixed(4)}</>
                          : <span className="italic">Awaiting first ping…</span>}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => unenroll.mutate(b.id)}
                      disabled={unenroll.isPending}
                      data-testid={`tata-unenroll-${b.code}`}
                    >
                      <XCircle size={14} className="mr-1 text-destructive" /> Unmap
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">No bowsers enrolled yet.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
